import Cookies from 'js-cookie'

/**
 * Session is the authenticated user record stored in a cookie. Mirrors the
 * `ghl_auth` shape used by platform-devtools-frontend so we can reuse the
 * same Authentik client registration without parallel infra.
 */
export interface AuthSession {
  id: string
  email: string
  name: string
  emailVerified?: boolean
  groups?: string[]
  /** Best-effort GHL location/tenant — falls back to 'loc-dev' for compat. */
  locationId?: string
}

const COOKIE_NAME =
  (import.meta.env.VITE_AUTH_COOKIE_NAME as string | undefined) ?? 'ghl_auth'
const COOKIE_DAYS = parseDays(import.meta.env.VITE_AUTH_COOKIE_DAYS as string | undefined)
const ALLOWED_DOMAIN =
  (import.meta.env.VITE_AUTH_ALLOWED_DOMAIN as string | undefined) ?? 'gohighlevel.com'

function parseDays(raw: string | undefined): number {
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 7
}

/** Save the user record to the auth cookie. HTTPS-only when served over TLS. */
export function saveSession(user: AuthSession): void {
  Cookies.set(COOKIE_NAME, JSON.stringify(user), {
    expires: COOKIE_DAYS,
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
    path: '/',
  })
}

/** Read the cookie. Returns null on missing or malformed payload. */
export function readSession(): AuthSession | null {
  const raw = Cookies.get(COOKIE_NAME)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isSession(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

export function clearSession(): void {
  Cookies.remove(COOKIE_NAME, { path: '/' })
}

/**
 * Domain gate — same policy as platform-devtools-frontend. Anyone outside
 * the allowed corporate domain is rejected before we save the cookie.
 */
export function isAllowedDomain(email: string | null | undefined): boolean {
  if (!email) return false
  const at = email.lastIndexOf('@')
  if (at <= 0) return false
  return email.slice(at + 1).toLowerCase() === ALLOWED_DOMAIN.toLowerCase()
}

function isSession(v: unknown): v is AuthSession {
  if (typeof v !== 'object' || v === null) return false
  const r = v as Record<string, unknown>
  return (
    typeof r.id === 'string' &&
    typeof r.email === 'string' &&
    typeof r.name === 'string'
  )
}

// ── Token storage (separate from user cookie) ─────────────────────────────
//
// Access + refresh + id tokens live in localStorage under a single key. We
// keep them out of the cookie so they aren't sent on every backend request
// (the Authorization header is the only place the backend reads from).

const TOKEN_KEY = 'teamofone.tokens'
const TOKEN_EXPIRY_SKEW_MS = 60_000

export interface StoredTokens {
  accessToken: string
  refreshToken?: string
  idToken?: string
  expiresAt?: number // epoch ms
}

export function isTokenExpired(
  tokens: Pick<StoredTokens, 'expiresAt'>,
  now = Date.now(),
): boolean {
  return (
    typeof tokens.expiresAt === 'number' &&
    Number.isFinite(tokens.expiresAt) &&
    tokens.expiresAt <= now + TOKEN_EXPIRY_SKEW_MS
  )
}

export function saveTokens(t: StoredTokens): void {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(t))
  } catch {
    /* storage unavailable — sign-in becomes session-only */
  }
}

export function readTokens(): StoredTokens | null {
  const tokens = readStoredTokens()
  if (!tokens) return null
  if (isTokenExpired(tokens)) {
    if (!tokens.refreshToken) clearTokens()
    return null
  }
  return tokens
}

export function readStoredTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const r = parsed as Record<string, unknown>
    if (typeof r.accessToken !== 'string' || r.accessToken.length === 0) {
      return null
    }
    const tokens: StoredTokens = {
      accessToken: r.accessToken,
      refreshToken: typeof r.refreshToken === 'string' ? r.refreshToken : undefined,
      idToken: typeof r.idToken === 'string' ? r.idToken : undefined,
      expiresAt:
        typeof r.expiresAt === 'number' && Number.isFinite(r.expiresAt)
          ? r.expiresAt
          : undefined,
    }
    return tokens
  } catch {
    return null
  }
}

export function hasUsableAccessToken(): boolean {
  return readTokens() !== null
}

export function clearTokens(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* no-op */
  }
}
